import React, { memo } from 'react';
import { Card, type CardProps } from 'antd';
import { theme } from '../../theme';
import { PAGE_PADDING } from '../../utils/constants';

interface StandardCardProps extends Omit<CardProps, 'styles'> {
  /** Contenido del card */
  children: React.ReactNode;
  /** Título del card */
  title?: React.ReactNode;
  /** Contenido adicional en el header (ej: botones) */
  extra?: React.ReactNode;
  /** Estilos personalizados para el body */
  bodyStyle?: React.CSSProperties;
  /** Padding del body (default: 24px) */
  bodyPadding?: string;
}

/**
 * Componente Card estándar con header oscuro y estilos consistentes
 * Usa theme.colors.primaryDark para el header
 * 
 * Optimizado con React.memo para evitar re-renders innecesarios
 * 
 * @example
 * <StandardCard title="Mi Título" extra={<Button>Acción</Button>}>
 *   Contenido
 * </StandardCard>
 */
export const StandardCard = memo<StandardCardProps>(({
  children,
  title,
  extra,
  bodyStyle = {},
  bodyPadding = PAGE_PADDING,
  style = {},
  ...restProps
}) => {
  return (
    <Card
      style={{
        backgroundColor: '#ffffff',
        borderRadius: theme.styles.borderRadius,
        boxShadow: theme.styles.boxShadow,
        maxWidth: '100%',
        ...style
      }}
      styles={{
        header: {
          backgroundColor: theme.colors.primaryDark,
          color: 'white',
          borderRadius: '8px 8px 0 0',
          fontSize: '16px',
          fontWeight: 600
        },
        body: {
          padding: bodyPadding,
          ...bodyStyle
        }
      }}
      title={title}
      extra={extra}
      {...restProps}
    >
      {children}
    </Card>
  );
});

StandardCard.displayName = 'StandardCard';

